/**
 * 固定 ledger action(s) 的子页（推荐奖励、结算、分红等菜单共用）。
 */
import { useState } from 'react';
import { DatePicker, Input, Select, Space, Tag } from 'antd';
import type { Dayjs } from 'dayjs';
import { DataTable } from '../../components/DataTable';
import { TOKEN_IDS, type TokenId } from '@uo/shared';

interface LedgerRow {
  id: string;
  user_id: number;
  asset: string;
  action: string;
  delta: string;
  balance_before: string;
  balance_after: string;
  ref: string | null;
  operator_id: number | null;
  note: string | null;
  create_time: string;
}

const ACTION_COLORS: Record<string, string> = {
  recharge: 'green',
  withdraw: 'volcano',
  admin_adjust: 'purple',
  ep_trade: 'blue',
  daily_settlement: 'geekblue',
  referral_bonus: 'cyan',
  level_bonus: 'gold',
  fork_event: 'purple',
  transfer_out: 'orange',
  transfer_in: 'lime',
};

export interface LedgerSubsetPageProps {
  title: string;
  /** 传给 GET /api/admin/ledger 的 `actions`（逗号分隔） */
  actions: string;
  subtitle?: string;
}

export function LedgerSubsetPage({ title, actions, subtitle }: LedgerSubsetPageProps) {
  const [userId, setUserId] = useState<string>('');
  const [asset, setAsset] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const columns = [
    { title: '流水号', dataIndex: 'id', width: 120 },
    { title: '时间', dataIndex: 'create_time', width: 170 },
    { title: '用户 ID', dataIndex: 'user_id', width: 90 },
    { title: '资产', dataIndex: 'asset', width: 90, render: (v: string) => v.toUpperCase() },
    {
      title: '动作',
      dataIndex: 'action',
      width: 150,
      render: (v: string) => <Tag color={ACTION_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: '变动',
      dataIndex: 'delta',
      width: 160,
      render: (v: string) => (
        <span style={{ color: v.startsWith('-') ? '#ff4d4f' : '#52c41a', fontFamily: 'monospace' }}>
          {v}
        </span>
      ),
    },
    { title: '变动前', dataIndex: 'balance_before', width: 160 },
    { title: '变动后', dataIndex: 'balance_after', width: 160 },
    { title: '关联 ref', dataIndex: 'ref' },
    { title: '备注', dataIndex: 'note' },
  ];

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>{title}</h2>
      {subtitle && <p style={{ color: '#666', marginBottom: 12 }}>{subtitle}</p>}
      <Space style={{ marginBottom: 12 }} wrap>
        <Input
          placeholder="用户 ID"
          style={{ width: 140 }}
          allowClear
          onChange={(e) => setUserId(e.target.value)}
        />
        <Select
          placeholder="资产"
          style={{ width: 140 }}
          allowClear
          onChange={setAsset}
          options={TOKEN_IDS.map((t: TokenId) => ({ value: t, label: t.toUpperCase() }))}
        />
        <DatePicker.RangePicker
          onChange={(v) => setRange(v as [Dayjs | null, Dayjs | null] | null)}
        />
      </Space>
      <DataTable<LedgerRow>
        queryKey={['admin', 'ledger-subset', actions, userId, asset ?? '', range?.[0]?.valueOf() ?? '']}
        endpoint="/api/admin/ledger"
        extraParams={{
          user_id: userId || undefined,
          asset,
          actions,
          start_date: range?.[0]?.format('YYYY-MM-DD'),
          end_date: range?.[1]?.format('YYYY-MM-DD'),
        }}
        columns={columns}
        rowKey="id"
        scroll={{ x: 1500 }}
      />
    </div>
  );
}
